import type { WithOptional } from 'admin';
import { Incoterms } from 'api';
import { useMemo } from 'react';

import { SelectField, type SelectFieldProps, type SelectOption } from '../SelectField/SelectField';

export const SHORT_INCOTERM_OPTIONS: SelectOption[] =
  Incoterms && Object.values(Incoterms).map((key) => ({ label: key, value: key }));

export const LONG_INCOTERM_OPTIONS: SelectOption[] = Incoterms && [
  { label: 'Ex Works (EXW)', value: Incoterms.EXW },
  { label: 'Free Carrier (FCA)', value: Incoterms.FCA },
  { label: 'Free Alongside Ship (FAS)', value: Incoterms.FAS },
  { label: 'Free on Board (FOB)', value: Incoterms.FOB },
  { label: 'Cost and Freight (CFR)', value: Incoterms.CFR },
  { label: 'Cost, Insurance & Freight (CIF)', value: Incoterms.CIF },
  { label: 'Carriage Paid To (CPT)', value: Incoterms.CPT },
  { label: 'Carriage & Insurance Paid (CIP)', value: Incoterms.CIP },
  { label: 'Delivered At Place (DAP)', value: Incoterms.DAP },
  { label: 'Delivery Duty Paid (DDP)', value: Incoterms.DDP },
];

type Props = {
  selectableIncoterms?: Incoterms[];
  shortVersion?: boolean;
} & Omit<WithOptional<SelectFieldProps, 'name' | 'required' | 'label'>, 'options'>;

export const IncotermSelectField = ({
  name = 'incoterm',
  required = true,
  label = 'Incoterm',
  selectableIncoterms,
  shortVersion = true,
  ...rest
}: Props) => {
  const incotermChoices = shortVersion ? SHORT_INCOTERM_OPTIONS : LONG_INCOTERM_OPTIONS;
  const options = useMemo(() => {
    if (!selectableIncoterms || selectableIncoterms.length === 0) {
      return incotermChoices;
    }
    if (!incotermChoices) {
      return [];
    }
    return incotermChoices.filter(({ value }) => selectableIncoterms.includes(value as Incoterms));
  }, [selectableIncoterms, incotermChoices]);

  return <SelectField label={label} name={name} required={required} options={options} {...rest} />;
};
